
import { supabase } from '@/lib/supabase-client';
import { Trip, Coordinates } from '@/types/trip';
import { toast } from 'sonner';

// Fetch all trips for the current user (including saved routes from tracks table)
export async function fetchTrips(): Promise<Trip[]> {
  try {
    console.log('Fetching trips from Supabase');
    
    // Get the current user first
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.warn('No authenticated user found when fetching trips');
      return [];
    }
    
    // Fetch trips for the current user
    // Try with user_id first (new schema), fallback to created_by (old schema)
    let { data: tripsData, error: tripsError } = await supabase
      .from('trips')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    
    // Fallback to old schema if user_id column doesn't exist
    if (tripsError && tripsError.message.includes('column "user_id" does not exist')) {
      ({ data: tripsData, error: tripsError } = await supabase
        .from('trips')
        .select('*')
        .eq('created_by', user.id)
        .order('created_at', { ascending: false }));
    }
    
    if (tripsError) {
      console.error('Supabase error when fetching trips:', tripsError);
      throw tripsError;
    }
    
    // Also fetch saved routes from tracks table
    // Get user's own tracks OR public visible tracks
    const { data: tracksData, error: tracksError } = await supabase
      .from('tracks')
      .select('*')
      .or(`created_by.eq.${user.id},and(is_public.eq.true,visible.eq.true)`)
      .order('created_at', { ascending: false });
    
    if (tracksError) {
      console.error('Error fetching saved routes:', tracksError);
      // Don't throw, just log the error and continue with trips only
    }
    
    console.log(`Retrieved ${tripsData?.length || 0} trips and ${tracksData?.length || 0} saved routes for user ${user.id}`);
    
    // Convert database trips to Trip interface
    const trips = (tripsData || []).map(trip => ({
      id: trip.id,
      user_id: trip.user_id || trip.created_by,
      title: trip.title || trip.name,
      description: trip.description || '',
      image_url: trip.image_url,
      start_date: trip.start_date,
      end_date: trip.end_date,
      // Handle new coordinate format
      start_coordinates: trip.start_coordinates || null,
      end_coordinates: trip.end_coordinates || null,
      difficulty: trip.difficulty || 'beginner',
      distance_km: trip.distance_km || trip.distance || 0,
      estimated_duration_hours: trip.estimated_duration_hours || trip.duration || 0,
      terrain_types: trip.terrain_types || [],
      visibility: trip.visibility || (trip.is_public ? 'public' : 'private'),
      trip_type: trip.trip_type || 'route',
      vehicle_requirements: trip.vehicle_requirements || {},
      weather_conditions: trip.weather_conditions || {},
      notes: trip.notes || '',
      is_completed: trip.is_completed || false,
      completion_date: trip.completion_date || null,
      rating: trip.rating || null,
      tags: trip.tags || [],
      metadata: trip.metadata || {},
      created_at: trip.created_at,
      updated_at: trip.updated_at || trip.created_at
    }));
    
    // Convert saved routes from tracks table to Trip interface
    const savedRoutes = (tracksData || []).map(track => {
      // Extract waypoints and calculate start/end locations
      const segments = track.segments || {};
      const points = segments.points || [];
      const firstPoint = points[0];
      const lastPoint = points[points.length - 1];
      
      return {
        id: track.id,
        user_id: track.created_by,
        title: track.name || 'Unnamed Route',
        description: track.metadata?.description || `Saved route with ${segments.waypoints?.length || 0} waypoints`,
        image_url: track.metadata?.imageUrl || null,
        start_date: track.created_at,
        end_date: track.created_at,
        start_coordinates: firstPoint ? { latitude: firstPoint.lat, longitude: firstPoint.lon } : null,
        end_coordinates: lastPoint ? { latitude: lastPoint.lat, longitude: lastPoint.lon } : null,
        start_location: firstPoint ? { latitude: firstPoint.lat, longitude: firstPoint.lon } : null,
        end_location: lastPoint ? { latitude: lastPoint.lat, longitude: lastPoint.lon } : null,
        difficulty: track.metadata?.difficulty || 'beginner',
        distance: track.distance_km || 0,
        distance_km: track.distance_km || 0,
        duration: track.metadata?.duration || 0,
        estimated_duration_hours: track.metadata?.duration || 0,
        terrain_types: [],
        visibility: track.is_public ? 'public' : 'private',
        trip_type: 'route',
        vehicle_requirements: {},
        weather_conditions: {},
        notes: track.metadata?.notes || '',
        is_completed: false,
        completion_date: null,
        rating: null,
        tags: [],
        metadata: track.metadata || {},
        created_at: track.created_at,
        updated_at: track.updated_at || track.created_at,
        created_by: track.created_by // Add for compatibility
      } as Trip;
    });
    
    // Combine trips and saved routes
    return [...trips, ...savedRoutes];
  } catch (error: any) {
    console.error('Error fetching trips:', error);
    toast.error('Failed to load trips: ' + (error.message || 'Unknown error'));
    return [];
  }
}

// Fetch a single trip by ID
export async function fetchTripById(tripId: string): Promise<Trip | null> {
  try {
    const { data, error } = await supabase
      .from('trips')
      .select('*')
      .eq('id', tripId)
      .single();
    
    if (error) {
      console.error('Error fetching trip by ID:', error);
      throw error;
    }
    
    if (!data) {
      return null;
    }
    
    // Convert to Trip interface with new schema support
    return {
      id: data.id,
      user_id: data.user_id || data.created_by,
      title: data.title || data.name,
      description: data.description || '',
      image_url: data.image_url,
      start_date: data.start_date,
      end_date: data.end_date,
      start_coordinates: data.start_coordinates || null,
      end_coordinates: data.end_coordinates || null,
      difficulty: data.difficulty || 'beginner',
      distance_km: data.distance_km || data.distance || 0,
      estimated_duration_hours: data.estimated_duration_hours || data.duration || 0,
      terrain_types: data.terrain_types || [],
      visibility: data.visibility || (data.is_public ? 'public' : 'private'),
      trip_type: data.trip_type || 'route',
      vehicle_requirements: data.vehicle_requirements || {},
      weather_conditions: data.weather_conditions || {},
      notes: data.notes || '',
      is_completed: data.is_completed || false,
      completion_date: data.completion_date || null,
      rating: data.rating || null,
      tags: data.tags || [],
      metadata: data.metadata || {},
      created_at: data.created_at,
      updated_at: data.updated_at || data.created_at
    };
  } catch (error: any) {
    console.error('Error fetching trip:', error);
    toast.error('Failed to load trip: ' + (error.message || 'Unknown error'));
    return null;
  }
}

// Create a new trip
export async function createTrip(tripData: Partial<Trip>): Promise<Trip | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast.error('You must be logged in to create a trip');
      return null;
    }
    
    const { data, error } = await supabase
      .from('trips')
      .insert({
        name: tripData.title,
        description: tripData.description,
        start_date: tripData.start_date,
        end_date: tripData.end_date,
        created_by: user.id,
        is_public: tripData.is_public || false,
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error creating trip:', error);
      throw error;
    }
    
    toast.success('Trip created successfully');
    
    // Convert to Trip interface with new schema support
    return {
      id: data.id,
      user_id: data.user_id || data.created_by,
      title: data.title || data.name,
      description: data.description || '',
      image_url: data.image_url,
      start_date: data.start_date,
      end_date: data.end_date,
      start_coordinates: data.start_coordinates || null,
      end_coordinates: data.end_coordinates || null,
      difficulty: data.difficulty || 'beginner',
      distance_km: data.distance_km || data.distance || 0,
      estimated_duration_hours: data.estimated_duration_hours || data.duration || 0,
      terrain_types: data.terrain_types || [],
      visibility: data.visibility || (data.is_public ? 'public' : 'private'),
      trip_type: data.trip_type || 'route',
      vehicle_requirements: data.vehicle_requirements || {},
      weather_conditions: data.weather_conditions || {},
      notes: data.notes || '',
      is_completed: data.is_completed || false,
      completion_date: data.completion_date || null,
      rating: data.rating || null,
      tags: data.tags || [],
      metadata: data.metadata || {},
      created_at: data.created_at,
      updated_at: data.updated_at || data.created_at
    };
  } catch (error: any) {
    console.error('Error creating trip:', error);
    toast.error('Failed to create trip: ' + (error.message || 'Unknown error'));
    return null;
  }
}

// Update an existing trip
export async function updateTrip(tripId: string, tripData: Partial<Trip>): Promise<Trip | null> {
  try {
    const { data, error } = await supabase
      .from('trips')
      .update({
        name: tripData.title,
        description: tripData.description,
        start_date: tripData.start_date,
        end_date: tripData.end_date,
        is_public: tripData.is_public,
      })
      .eq('id', tripId)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating trip:', error);
      throw error;
    }
    
    toast.success('Trip updated successfully');
    
    // Convert to Trip interface with new schema support
    return {
      id: data.id,
      user_id: data.user_id || data.created_by,
      title: data.title || data.name,
      description: data.description || '',
      image_url: data.image_url,
      start_date: data.start_date,
      end_date: data.end_date,
      start_coordinates: data.start_coordinates || null,
      end_coordinates: data.end_coordinates || null,
      difficulty: data.difficulty || 'beginner',
      distance_km: data.distance_km || data.distance || 0,
      estimated_duration_hours: data.estimated_duration_hours || data.duration || 0,
      terrain_types: data.terrain_types || [],
      visibility: data.visibility || (data.is_public ? 'public' : 'private'),
      trip_type: data.trip_type || 'route',
      vehicle_requirements: data.vehicle_requirements || {},
      weather_conditions: data.weather_conditions || {},
      notes: data.notes || '',
      is_completed: data.is_completed || false,
      completion_date: data.completion_date || null,
      rating: data.rating || null,
      tags: data.tags || [],
      metadata: data.metadata || {},
      created_at: data.created_at,
      updated_at: data.updated_at || data.created_at
    };
  } catch (error: any) {
    console.error('Error updating trip:', error);
    toast.error('Failed to update trip: ' + (error.message || 'Unknown error'));
    return null;
  }
}

// Delete a trip
export async function deleteTrip(tripId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('trips')
      .delete()
      .eq('id', tripId);
    
    if (error) {
      console.error('Error deleting trip:', error);
      throw error;
    }
    
    toast.success('Trip deleted successfully');
    return true;
  } catch (error: any) {
    console.error('Error deleting trip:', error);
    toast.error('Failed to delete trip: ' + (error.message || 'Unknown error'));
    return false;
  }
}

// Share a trip with users and/or groups
export async function shareTrip(
  tripId: string, 
  sharedWithUsers?: string[], 
  sharedWithGroups?: string[]
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast.error('You must be logged in to share trips');
      return false;
    }

    // Update the trip's sharing arrays
    const { error: updateError } = await supabase
      .from('trips')
      .update({
        shared_with_users: sharedWithUsers || [],
        shared_with_groups: sharedWithGroups || []
      })
      .eq('id', tripId)
      .eq('user_id', user.id); // Ensure user owns the trip

    if (updateError) {
      console.error('Error updating trip sharing:', updateError);
      throw updateError;
    }

    // Create individual share records for tracking
    const sharePromises = [];

    // Add user shares
    if (sharedWithUsers && sharedWithUsers.length > 0) {
      for (const userId of sharedWithUsers) {
        sharePromises.push(
          supabase.from('trip_shares').upsert({
            trip_id: tripId,
            shared_with_user_id: userId,
            shared_by: user.id
          }, {
            onConflict: 'trip_id,shared_with_user_id'
          })
        );
      }
    }

    // Add group shares
    if (sharedWithGroups && sharedWithGroups.length > 0) {
      for (const groupId of sharedWithGroups) {
        sharePromises.push(
          supabase.from('trip_shares').upsert({
            trip_id: tripId,
            shared_with_group_id: groupId,
            shared_by: user.id
          }, {
            onConflict: 'trip_id,shared_with_group_id'
          })
        );
      }
    }

    // Execute all share operations
    if (sharePromises.length > 0) {
      const results = await Promise.all(sharePromises);
      const hasErrors = results.some(r => r.error);
      
      if (hasErrors) {
        console.error('Some shares failed:', results.filter(r => r.error));
        toast.error('Some shares could not be created');
        return false;
      }
    }

    toast.success('Trip shared successfully');
    return true;
  } catch (error: any) {
    console.error('Error sharing trip:', error);
    toast.error('Failed to share trip: ' + (error.message || 'Unknown error'));
    return false;
  }
}

// Get trips shared with the current user
export async function fetchSharedTrips(): Promise<Trip[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.warn('No authenticated user found when fetching shared trips');
      return [];
    }
    
    // Use the database function to get shared trips
    const { data, error } = await supabase.rpc('get_shared_trips', {
      p_user_id: user.id
    });
    
    if (error) {
      console.error('Error fetching shared trips:', error);
      throw error;
    }
    
    console.log(`Retrieved ${data?.length || 0} shared trips for user ${user.id}`);
    
    // Convert to Trip interface
    return (data || []).map((trip: any) => ({
      id: trip.id,
      user_id: trip.owner_id,
      title: trip.name,
      description: trip.description || '',
      difficulty: trip.difficulty || 'beginner',
      distance_km: trip.distance_km || 0,
      visibility: 'shared',
      shared_via: trip.shared_via,
      owner_name: trip.owner_name,
      created_at: trip.created_at,
      // Set defaults for other fields
      image_url: null,
      start_date: null,
      end_date: null,
      start_coordinates: null,
      end_coordinates: null,
      estimated_duration_hours: 0,
      terrain_types: [],
      trip_type: 'route',
      vehicle_requirements: {},
      weather_conditions: {},
      notes: '',
      is_completed: false,
      completion_date: null,
      rating: null,
      tags: [],
      metadata: {},
      updated_at: trip.created_at
    }));
  } catch (error: any) {
    console.error('Error fetching shared trips:', error);
    toast.error('Failed to load shared trips: ' + (error.message || 'Unknown error'));
    return [];
  }
}
